import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PlayerRatingData {
  currentRating: number | null;      // Live rating from snapshot
  avgRating: number | null;          // Historical average
  medianRating: number | null;       // Historical median
  ratingP25: number | null;          // 25th percentile (below expected threshold)
  ratingP75: number | null;          // 75th percentile (above expected threshold)
  gamesPlayed: number;               // Historical games count
  trend: 'up' | 'normal' | 'down' | null;  // Performance vs expected
  snapshotMinute: number | null;     // When the rating was captured
}

export type PlayerRatingsMap = Map<number, PlayerRatingData>;

export interface PlayerRatingsResult {
  ratings: PlayerRatingsMap;
  hasFinishedSnapshots: boolean;  // TRUE if snapshots with minute >= 90 exist
}

interface HistoricalData {
  player_api_id: number;
  avg_rating: number | null;
  median_rating: number | null;
  rating_p25: number | null;
  rating_p75: number | null;
  games_played: number | null;
}

interface SnapshotData {
  player_api_id: number;
  rating: number | null;
  snapshot_minute: number;
}

/**
 * Calculate performance trend based on current rating vs historical percentiles
 */
function calculateTrend(
  current: number | null,
  p25: number | null,
  p75: number | null
): 'up' | 'normal' | 'down' | null {
  if (current === null || p25 === null || p75 === null) return null;
  if (current >= p75) return 'up';    // Above top 25% = excellent
  if (current <= p25) return 'down';  // Below bottom 25% = poor
  return 'normal';                     // Within expected range
}

/**
 * Build ratings map from live snapshots and historical data
 */
function buildRatingsMap(
  snapshots: SnapshotData[] | null,
  historical: HistoricalData[] | null
): PlayerRatingsMap {
  const map = new Map<number, PlayerRatingData>();
  
  // First, add historical data for all players
  if (historical) {
    for (const h of historical) {
      map.set(h.player_api_id, {
        currentRating: null,
        avgRating: h.avg_rating,
        medianRating: h.median_rating,
        ratingP25: h.rating_p25,
        ratingP75: h.rating_p75,
        gamesPlayed: h.games_played || 0,
        trend: null,
        snapshotMinute: null,
      });
    }
  }
  
  // Then, overlay live snapshot data (most recent per player)
  if (snapshots) {
    // Group by player, keep most recent snapshot
    const latestByPlayer = new Map<number, SnapshotData>();
    for (const s of snapshots) {
      const existing = latestByPlayer.get(s.player_api_id);
      if (!existing || s.snapshot_minute > existing.snapshot_minute) {
        latestByPlayer.set(s.player_api_id, s);
      }
    }
    
    // Merge with historical
    for (const [playerId, snapshot] of latestByPlayer) {
      const existing = map.get(playerId);
      if (existing) {
        existing.currentRating = snapshot.rating;
        existing.snapshotMinute = snapshot.snapshot_minute;
        existing.trend = calculateTrend(snapshot.rating, existing.ratingP25, existing.ratingP75);
      } else {
        // Player has live data but no historical
        map.set(playerId, {
          currentRating: snapshot.rating,
          avgRating: null,
          medianRating: null,
          ratingP25: null,
          ratingP75: null,
          gamesPlayed: 0,
          trend: null,
          snapshotMinute: snapshot.snapshot_minute,
        });
      }
    }
  }
  
  return map;
}

/**
 * Hook to fetch player ratings (live + historical) for lineup display
 * 
 * @param fixtureId - The fixture ID to fetch live snapshots for
 * @param playerApiIds - Array of player API IDs to fetch historical data for
 * @param isLive - Whether the game is currently live (enables polling)
 * @param isFinished - Whether the game has finished (fetch final ratings)
 */
export function usePlayerRatings(
  fixtureId: number | null,
  playerApiIds: number[],
  isLive: boolean,
  isFinished: boolean = false
) {
  return useQuery({
    // Simplified key - no state flags to avoid cache fragmentation
    queryKey: ['player-ratings', fixtureId, playerApiIds.sort().join(','), isLive],
    queryFn: async (): Promise<PlayerRatingsResult> => {
      const validPlayerIds = playerApiIds.filter(id => id && id > 0);
      
      console.log('[usePlayerRatings] Starting fetch:', {
        fixtureId,
        isLive,
        isFinished,
        playerCount: validPlayerIds.length
      });
      
      if (validPlayerIds.length === 0) {
        console.log('[usePlayerRatings] No valid player IDs, returning empty result');
        return { ratings: new Map(), hasFinishedSnapshots: false };
      }
      
      // Fetch historical data for all players
      const { data: historicalData, error: historicalError } = await supabase
        .from('player_performance_summary')
        .select('player_api_id, avg_rating, median_rating, rating_p25, rating_p75, games_played')
        .in('player_api_id', validPlayerIds);
      
      if (historicalError) {
        console.error('[usePlayerRatings] Error fetching historical:', historicalError);
      }
      
      console.log('[usePlayerRatings] Historical data loaded:', historicalData?.length ?? 0);
      
      // Always fetch snapshots if fixtureId exists - they may exist for finished games
      let snapshotsData: SnapshotData[] | null = null;
      
      if (fixtureId) {
        const { data, error: snapshotsError } = await supabase
          .from('player_match_snapshots')
          .select('player_api_id, rating, snapshot_minute')
          .eq('fixture_id', fixtureId)
          .in('player_api_id', validPlayerIds)
          .order('snapshot_minute', { ascending: false });
        
        if (snapshotsError) {
          console.error('[usePlayerRatings] Error fetching snapshots:', snapshotsError);
        }
        snapshotsData = data as SnapshotData[] | null;
        
        console.log('[usePlayerRatings] Snapshots loaded:', {
          count: snapshotsData?.length ?? 0,
          fixtureId,
          sampleRatings: snapshotsData?.slice(0, 3).map(s => ({
            player: s.player_api_id,
            rating: s.rating,
            minute: s.snapshot_minute
          }))
        });
      }
      
      const ratingsMap = buildRatingsMap(
        snapshotsData,
        historicalData as HistoricalData[] | null
      );
      
      // Detect if game has finished data (snapshots with minute >= 90)
      const hasFinishedSnapshots = snapshotsData?.some(s => s.snapshot_minute >= 90) ?? false;
      
      console.log('[usePlayerRatings] Final result:', {
        mapSize: ratingsMap.size,
        hasFinishedSnapshots,
        maxMinute: snapshotsData?.reduce((max, s) => Math.max(max, s.snapshot_minute), 0) ?? 0
      });
      
      return { ratings: ratingsMap, hasFinishedSnapshots };
    },
    enabled: playerApiIds.length > 0,
    staleTime: isLive ? 20_000 : 5 * 60_000, // 20s if live, 5min otherwise
    refetchInterval: isLive ? 30_000 : false, // Poll every 30s during live (snapshots every 5min)
    refetchOnMount: isLive ? 'always' : true, // Force refetch when mounting in live mode
    refetchOnWindowFocus: isLive, // Refetch when window regains focus during live
  });
}

/**
 * Helper to get rating color class based on value
 */
export function getRatingColorClass(rating: number | null): string {
  if (rating === null) return 'text-muted-foreground';
  if (rating >= 7.5) return 'text-emerald-600 dark:text-emerald-400';
  if (rating >= 6.5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Helper to get trend indicator
 */
export function getTrendIndicator(trend: 'up' | 'normal' | 'down' | null): {
  icon: string;
  colorClass: string;
  label: string;
} {
  switch (trend) {
    case 'up':
      return { 
        icon: '↑', 
        colorClass: 'text-emerald-600 dark:text-emerald-400',
        label: 'Above expected'
      };
    case 'down':
      return { 
        icon: '↓', 
        colorClass: 'text-red-600 dark:text-red-400',
        label: 'Below expected'
      };
    case 'normal':
      return { 
        icon: '→', 
        colorClass: 'text-muted-foreground',
        label: 'As expected'
      };
    default:
      return { icon: '', colorClass: '', label: '' };
  }
}
